import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  getTransportStockHistory,
  getTransportStockRows,
  importTransportStock,
} from '../../../api/transport-stockApi';
import { getApiErrorMessage } from '../../../api/mix-deliveriesApi';

const AREAS = { AWH: 'AWH', TWH: 'TWH', CONVEYOR: 'Transporter' };
const QUALITY = {
  UNRESTRICTED: 'Nieograniczony',
  QUALITY_INSPECTION: 'Kontrola jakości',
};
const number = (value) =>
  new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 3 }).format(
    Number(value),
  );
const date = (value) =>
  value ? value.slice(0, 10).split('-').reverse().join('.') : '—';

export default function TransportStockPage({ userRole, embedded = false }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [quality, setQuality] = useState('');
  const [area, setArea] = useState('');
  const [availability, setAvailability] = useState('AVAILABLE');
  const [importId, setImportId] = useState('');
  const [result, setResult] = useState({ items: [], total: 0, snapshot: null });
  const [loading, setLoading] = useState(true);
  const fileInput = useRef(null);
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getTransportStockRows({
      page: page + 1,
      limit,
      search: search || undefined,
      quality: quality || undefined,
      area: area || undefined,
      availability,
      importId: importId || undefined,
    })
      .then((data) => {
        if (!active) return;
        const lastPage = Math.max(0, Math.ceil(data.total / limit) - 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setResult(data);
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, limit, search, quality, area, availability, importId, refresh]);

  useEffect(() => {
    let active = true;
    setHistoryLoading(true);
    setHistoryError('');
    getTransportStockHistory({ page: historyPage + 1, limit: 10 })
      .then((data) => {
        if (active) setHistory(data);
      })
      .catch((err) => {
        if (active) setHistoryError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [historyPage, refresh]);

  const resetView = () => {
    setImportId('');
    setPage(0);
    setSearch('');
    setSearchInput('');
    setQuality('');
    setArea('');
    setAvailability('AVAILABLE');
  };
  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setNotice('');
    try {
      const snapshot = await importTransportStock(file);
      setNotice(
        `Zaimportowano ${snapshot.rowCount} pozycji. Nowy zapas jest aktualny.`,
      );
      setFile(null);
      if (fileInput.current) fileInput.current.value = '';
      resetView();
      setHistoryPage(0);
      setRefresh((value) => value + 1);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };
  const changeFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };
  const snapshot = result.snapshot;
  const totals = snapshot?.summary?.totals ?? [];

  return (
    <Stack spacing={2} sx={{ p: embedded ? 0 : 3 }}>
      <Typography variant={embedded ? 'h6' : 'h4'}>
        Zapas do symulacji transportów
      </Typography>
      <Typography color="text.secondary">
        Import dostępnego zapasu z terminami przydatności. Kontrola jakości
        pozostaje osobną pulą.
      </Typography>
      {['ADMIN', 'KONTROLER'].includes(userRole) && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography>
              Nowy poprawny import zastępuje aktualny stan magazynu. Poprzednie
              importy pozostają w historii.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx"
                aria-label="Plik zapasu"
                disabled={uploading}
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              <Button
                variant="contained"
                disabled={!file || uploading}
                onClick={upload}
              >
                {uploading ? 'Importowanie…' : 'Importuj zapas'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      {importId && (
        <Alert severity="info">
          Podgląd wybranego importu.{' '}
          <Button
            onClick={() => {
              resetView();
              setRefresh((v) => v + 1);
            }}
          >
            Pokaż aktualny zapas
          </Button>
        </Alert>
      )}
      {!loading && !error && !snapshot && (
        <Alert severity="info">Brak importu zapasu dla Twojego magazynu.</Alert>
      )}
      {snapshot && !loading && !error && (
        <>
          <Typography>
            Magazyn {snapshot.warehouse} · {snapshot.fileName} ·{' '}
            {new Date(snapshot.createdAt).toLocaleString('pl-PL')}
          </Typography>
          <Typography variant="body2">
            Wiersze źródłowe: {snapshot.sourceRows}. Zapas bez rezerwacji:{' '}
            {snapshot.summary.availableRows} pozycji. Zarezerwowane:{' '}
            {snapshot.summary.reservedRows} pozycji — wyłączone z dostępności.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Podsumowanie zapasu">
              <TableHead>
                <TableRow>
                  <TableCell>Pula</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Obszar</TableCell>
                  <TableCell>Jednostka</TableCell>
                  <TableCell align="right">Ilość</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {totals.map((total) => (
                  <TableRow
                    key={`${total.isReserved}-${total.quality}-${total.area}-${total.unit}`}
                  >
                    <TableCell>
                      {total.isReserved
                        ? 'Zarezerwowany — wykluczony'
                        : 'Bez rezerwacji'}
                    </TableCell>
                    <TableCell>{QUALITY[total.quality]}</TableCell>
                    <TableCell>{AREAS[total.area]}</TableCell>
                    <TableCell>{total.unit}</TableCell>
                    <TableCell align="right">
                      {number(total.quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {snapshot.summary.reservedRows > 0 && (
            <Alert severity="info">
              Zapas z PDO lub numerem dokumentu jest zarezerwowany. Wybierz
              „Zarezerwowane”, aby sprawdzić wykluczone pozycje.
            </Alert>
          )}
        </>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField
          size="small"
          label="Produkt, opis, bin lub partia"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput.trim());
              setPage(0);
            }
          }}
        />
        <Button
          onClick={() => {
            setSearch(searchInput.trim());
            setPage(0);
          }}
        >
          Szukaj
        </Button>
        <TextField
          select
          size="small"
          label="Status"
          value={quality}
          onChange={changeFilter(setQuality)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Wszystkie statusy</MenuItem>
          {Object.entries(QUALITY).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Obszar"
          value={area}
          onChange={changeFilter(setArea)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">Wszystkie obszary</MenuItem>
          {Object.entries(AREAS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Rezerwacje"
          value={availability}
          onChange={changeFilter(setAvailability)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="AVAILABLE">Bez rezerwacji</MenuItem>
          <MenuItem value="RESERVED">Zarezerwowane</MenuItem>
        </TextField>
        <Button
          disabled={uploading}
          onClick={() => {
            resetView();
            setRefresh((v) => v + 1);
          }}
        >
          Odśwież / wyczyść
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Pozycje zapasu">
          <TableHead>
            <TableRow>
              {[
                'Produkt',
                'Opis',
                'Ilość',
                'JM',
                'Ilość alternatywna',
                'Skład / rodzaj',
                'Status',
                'Obszar',
                'Typ / bin',
                'Partia',
                'Termin',
                'HU',
                'Dokument',
              ].map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : error ? null : (
              result.items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.materialNumber}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell align="right">{number(row.quantity)}</TableCell>
                  <TableCell>{row.unit}</TableCell>
                  <TableCell>
                    {row.alternativeQuantity != null
                      ? `${number(row.alternativeQuantity)} ${
                          row.alternativeUnit ?? ''
                        }`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {row.storageLocation} / {row.stockType}
                  </TableCell>
                  <TableCell>{QUALITY[row.quality]}</TableCell>
                  <TableCell>{AREAS[row.area]}</TableCell>
                  <TableCell>
                    {row.storageType} / {row.bin}
                  </TableCell>
                  <TableCell>{row.batch || '—'}</TableCell>
                  <TableCell>{date(row.expiresAt)}</TableCell>
                  <TableCell>{row.handlingUnit || '—'}</TableCell>
                  <TableCell>
                    {[row.documentType, row.documentNumber]
                      .filter(Boolean)
                      .join(' ') || '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
            {!loading && !error && result.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={13}>
                  Brak pozycji spełniających filtry.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={result.total}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Pozycji na stronie:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setLimit(Number(e.target.value));
          setPage(0);
        }}
        backIconButtonProps={{ disabled: loading || page === 0 }}
        nextIconButtonProps={{
          disabled: loading || (page + 1) * limit >= result.total,
        }}
      />
      <Typography variant="h6">Historia importów</Typography>
      {historyError && <Alert severity="error">{historyError}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data importu</TableCell>
              <TableCell>Plik</TableCell>
              <TableCell>Pozycje</TableCell>
              <TableCell>Podgląd</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!historyLoading &&
              history.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {new Date(item.createdAt).toLocaleString('pl-PL')}
                  </TableCell>
                  <TableCell>{item.fileName}</TableCell>
                  <TableCell>{item.rowCount}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        setImportId(item.id);
                        setPage(0);
                      }}
                    >
                      Pokaż
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {historyLoading && (
        <Box>
          <CircularProgress size={24} />
        </Box>
      )}
      <TablePagination
        component="div"
        count={history.total}
        page={historyPage}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        labelRowsPerPage="Importów na stronie:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        onPageChange={(_, value) => setHistoryPage(value)}
        backIconButtonProps={{ disabled: historyLoading || historyPage === 0 }}
        nextIconButtonProps={{
          disabled: historyLoading || (historyPage + 1) * 10 >= history.total,
        }}
      />
    </Stack>
  );
}
